/**
 * regionalApi — typed fetchers for the 6 Regional Director bucket endpoints
 * + React Query hooks. The shapes mirror the backend's
 * `regional-dashboards.service.ts` DTOs.
 *
 * One React Query key per (regionSlug, bucket). Stale-time defaults to
 * 30s for the operational reads; the warehouse-cache layer behind the
 * heavier rollups has its own 6-hour TTL so the network round-trip is
 * the only cost on a cold key.
 */
import type { CrewRole } from '@bonsai/shared';
import { useQuery } from '@tanstack/react-query';

import type { AvailabilitySlot } from '../../../api/availability';
import type { OpenShiftWriteRow, ShiftRequester } from '../../../api/openShifts';
import type { WorkshopLifecycle } from '../../../api/workshops';
import { apiFetch } from '../../../lib/apiFetch';

export type DashboardMode = 'live' | 'read-only';

/** A region the requester can switch the dashboard to (drives the eyebrow picker). */
export interface RegionRef {
  id: string;
  slug: string;
  name: string;
}

export interface DashboardEnvelope<T> {
  mode: DashboardMode;
  scope: {
    regionId: string;
    regionSlug: string;
    regionName: string;
    /** Regions the requester can switch to — defaults to [] for older payloads. */
    regions?: RegionRef[];
  };
  data: T;
}

export interface KpiCell {
  label: string;
  value: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  spark?: number[];
}

export interface ActionItem {
  id: string;
  severity: 'urgent' | 'warn' | 'info';
  title: string;
  href?: string;
}

/** One workshop as the calendar consumes it (overview "this week" + the range fetch). */
export interface RegionalWorkshopEntry {
  id: string;
  /** Publication lifecycle (ADR-0014). The shift-board picker fetches only
   *  'active' (BON-474); the calendar shows every lifecycle. */
  lifecycle: WorkshopLifecycle;
  /** Absolute instant (UTC ISO). Sort/compare only — never displayed (D10). */
  date: string;
  /** Venue-local wall clock (naive ISO, no `Z`) for display. Null when unset. */
  localDate: string | null;
  venueName: string;
  venueShort?: string;
  /** Null for a venueless private event — its region comes from the workshop
   *  row itself rather than a venue lookup. */
  venueId: string | null;
  capacity: number;
  booked: number;
  treeSpecies: string | null;
  leadInstructor: string | null;
  assistant?: string | null;
  /** The signed-in viewer is on the crew. */
  isMine?: boolean;
  /** Their part on it; null when not on it, or in a position with no role. */
  myRole?: CrewRole | null;
  accentColor?: string;
  subregionLabel?: string;
  regionSlug?: string | null;
  status?: 'sold-out' | 'on-track' | 'soft' | 'open';
  replacements?: number;
  /** True for a private event — never on public sale, and possibly venue-less
   *  (the calendar select resolves its region via COALESCE). */
  isPrivate?: boolean;
}

export interface RegionalOverviewDto {
  kpis: KpiCell[];
  weekStartIso?: string;
  workshopsThisWeek: RegionalWorkshopEntry[];
  actionItems: ActionItem[];
}

/** Payload of the calendar range endpoint (`/regional/:slug/workshops?from=&to=`). */
export interface RegionalWorkshopsDto {
  workshops: RegionalWorkshopEntry[];
}

/** One upcoming workshop's pace vs the linear expected-fill curve. */
export interface SalesPaceRow {
  dateIso: string;
  venueShort: string;
  label: string;
  sold: number;
  cap: number;
  fillPct: number;
  expectedPct: number;
  status: 'on-pace' | 'soft' | 'risk';
}

export interface RegionalSalesDto {
  kpis: KpiCell[];
  funnel: { sessions: number; selects: number; checkouts: number; purchases: number };
  funnelSpark?: Array<{ label: string; value: number }>;
  perVenue: Array<{ venueId: string; venueName: string; sold: number; capacity: number }>;
  channels: Array<{ channel: string; sessions: number; purchases: number }>;
  /** Null on any of these → source unavailable → module keeps MockedOverlay. */
  weekly: {
    revenueCents: number;
    revenueDeltaPct: number | null;
    ticketsSold: number;
    ticketCap: number;
  } | null;
  pacing: { onPace: number; soft: number; atRisk: number; rows: SalesPaceRow[] } | null;
  problemWorkshops: Array<SalesPaceRow & { city: string | null; diffPct: number }> | null;
  adSpend: {
    weekSpendCents: number;
    prevWeekSpendCents: number;
    platforms: Array<{
      platform: string;
      spendCents: number;
      conversions: number;
      cpaCents: number | null;
      sharePct: number;
    }>;
  } | null;
}

export interface TeacherEntry {
  userId: string;
  name: string;
  role: 'teacher' | 'assistant' | 'teacher_in_training';
  ratePerHr: number | null;
  workshopsThisWeek: number;
  workshopsMtd: number;
  /** POS sales this month, in cents. Null → warehouse unavailable. */
  posSalesMtd: number | null;
  availability: 'active' | 'pto';
  avatarColor?: string;
}

export interface RegionalTeachersDto {
  kpis: KpiCell[];
  teachers: TeacherEntry[];
  assistants: Array<{
    userId: string;
    name: string;
    ratePerHr: number | null;
    shiftsThisWeek: number;
    avatarColor?: string;
  }>;
  /** userId → 7-cell Sun..Sat availability grid. */
  /** userId → 7 Sun..Sat cells. `shift` = assigned; the slot values are what
   *  the teacher declared (BON-241); `null` = not set. */
  availability: Record<string, Array<'shift' | AvailabilitySlot | null>>;
  /** How many rostered teachers have declared anything — lets the grid read
   *  as "not filled in yet" rather than "nobody is free". Optional so an
   *  older backend DTO during the deploy window still renders. */
  availabilityDeclaredCount?: number;
  /** Upcoming workshops with no teacher assigned (next 14 days). */
  openShifts: Array<{
    startsAtIso: string;
    venueShort: string;
    role: string;
    urgent: boolean;
  }>;
}

/** One published open shift — the same row the write api returns
 *  (mirrors OpenShiftEntry in backend/src/services/open-shifts.service.ts),
 *  aliased so the board type and the publish/withdraw responses can't drift. */
export type OpenShiftBoardEntry = OpenShiftWriteRow;

/** Shift board tab of the Teachers bucket (BON-240) — explicitly published
 *  open shifts only; unstaffed workshops never auto-appear here. */
export interface RegionalShiftBoardDto {
  shifts: OpenShiftBoardEntry[];
  /** Pending requesters keyed by open-shift id (BON-243). A shift nobody has
   *  asked for is absent from the map, not present with an empty array. */
  pendingRequests: Record<string, ShiftRequester[]>;
  /** Upcoming filled shifts — its own count because `shifts` is live-only, so
   *  a filled shift has already left the board. */
  filledCount: number;
}

export interface RegionalSuppliesDto {
  kpis: KpiCell[];
  // Inventory-dependent surface — rendered behind a MockedOverlay until
  // the inventory module lands (BON-151).
  mocked: true;
}

export interface RegionalCorpDto {
  kpis: KpiCell[];
  // pipeline is read by RegionalOverviewRightRail's Corp Pipeline preview.
  pipeline: Array<{ stage: string; count: number }>;
  mocked: true;
}

export type RegionalBucket =
  | 'overview'
  | 'sales'
  | 'teachers'
  | 'shifts'
  | 'supplies'
  | 'venues'
  | 'corp';

/**
 * Error thrown when a dashboards bucket fetch fails. Captures the parsed
 * server error envelope ({ status, message }) when present, so BucketError
 * can show the spec'd message instead of a generic HTTP status. Falls back
 * to a synthesized message when the body isn't JSON.
 */
export class BucketFetchError extends Error {
  readonly httpStatus: number;
  readonly bucket: RegionalBucket;
  constructor(bucket: RegionalBucket, httpStatus: number, message: string) {
    super(message);
    this.name = 'BucketFetchError';
    this.httpStatus = httpStatus;
    this.bucket = bucket;
  }
}

async function fetchBucket<T>(
  regionSlug: string,
  bucket: RegionalBucket,
): Promise<DashboardEnvelope<T>> {
  const res = await apiFetch(`/api/v1/dashboards/regional/${regionSlug}/${bucket}`);
  if (!res.ok) {
    // Try the spec'd error envelope first; fall back to a synthesized message.
    let message: string;
    try {
      const body = (await res.json()) as { status?: string; message?: string };
      message = body?.message ?? `Failed to load ${bucket} (HTTP ${res.status})`;
    } catch {
      message = `Failed to load ${bucket} (HTTP ${res.status} ${res.statusText})`;
    }
    throw new BucketFetchError(bucket, res.status, message);
  }
  return (await res.json()) as DashboardEnvelope<T>;
}

const STALE_30S = 30_000;

export function useRegionalOverview(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'overview'],
    queryFn: () => fetchBucket<RegionalOverviewDto>(regionSlug, 'overview'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

export function useRegionalSales(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'sales'],
    queryFn: () => fetchBucket<RegionalSalesDto>(regionSlug, 'sales'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

export function useRegionalTeachers(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'teachers'],
    queryFn: () => fetchBucket<RegionalTeachersDto>(regionSlug, 'teachers'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

export function useRegionalShiftBoard(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'shifts'],
    queryFn: () => fetchBucket<RegionalShiftBoardDto>(regionSlug, 'shifts'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

export function useRegionalSupplies(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'supplies'],
    queryFn: () => fetchBucket<RegionalSuppliesDto>(regionSlug, 'supplies'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

export function useRegionalCorp(regionSlug: string) {
  return useQuery({
    queryKey: ['dashboards.regional', regionSlug, 'corp'],
    queryFn: () => fetchBucket<RegionalCorpDto>(regionSlug, 'corp'),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug),
  });
}

async function fetchWorkshopsInRange(
  regionSlug: string,
  fromDate: string,
  toDate: string,
  lifecycle?: WorkshopLifecycle,
): Promise<RegionalWorkshopEntry[]> {
  const qs = new URLSearchParams({
    from: fromDate,
    to: toDate,
    ...(lifecycle ? { lifecycle } : {}),
  }).toString();
  const res = await apiFetch(`/api/v1/dashboards/regional/${regionSlug}/workshops?${qs}`);
  if (!res.ok) throw new Error(`Failed to load workshops (HTTP ${res.status})`);
  const env = (await res.json()) as DashboardEnvelope<RegionalWorkshopsDto>;
  return env.data.workshops;
}

/**
 * Workshops starting on the given dates (`YYYY-MM-DD`, both ends included). Keyed by [from, to]
 * so navigating months/weeks refetches (fetch-on-navigate, BON-196). Disabled
 * until a range is known.
 */
export function useRegionalWorkshops(
  regionSlug: string,
  fromDate?: string,
  toDate?: string,
  lifecycle?: WorkshopLifecycle,
) {
  return useQuery({
    queryKey: [
      'dashboards.regional',
      regionSlug,
      'workshops',
      fromDate,
      toDate,
      lifecycle ?? 'all',
    ],
    queryFn: () =>
      fetchWorkshopsInRange(regionSlug, fromDate as string, toDate as string, lifecycle),
    staleTime: STALE_30S,
    enabled: Boolean(regionSlug && fromDate && toDate),
  });
}
